import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, Clock, CheckCircle, Play, Search, 
  Download, Filter, TrendingUp
} from 'lucide-react';
import { courseService } from '../../../services/courseService';

export const MyLearning = ({ bookId, user }) => {
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [inProgressCourses, setInProgressCourses] = useState([]);
  const [completedCourses, setCompletedCourses] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadMyCourses();
    }
  }, [user, bookId]);

  const loadMyCourses = async () => {
    try {
      // Get all user progress
      const progressData = await courseService.getUserAllProgress(user.uid, bookId);
      
      // Get enrolled course IDs
      const enrolledIds = progressData.map(p => p.courseId);
      
      // Load course details
      const coursePromises = enrolledIds.map(id => 
        courseService.getCourse(bookId, id)
      );
      const courses = await Promise.all(coursePromises);
      
      // Combine courses with progress
      const coursesWithProgress = courses.map(course => {
        const progress = progressData.find(p => p.courseId === course.id);
        return {
          ...course,
          progress: progress || { completedLessons: [], completionPercentage: 0 }
        };
      });

      setEnrolledCourses(coursesWithProgress);

      // Separate by status
      const completed = coursesWithProgress.filter(c => 
        c.progress.completionPercentage === 100
      );
      
      const inProgress = coursesWithProgress.filter(c => 
        c.progress.completionPercentage > 0 && 
        c.progress.completionPercentage < 100
      );

      setCompletedCourses(completed);
      setInProgressCourses(inProgress);

    } catch (error) {
      console.error('Error loading courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredCourses = () => {
    let courses = [];
    
    switch (activeTab) {
      case 'in-progress':
        courses = inProgressCourses;
        break;
      case 'completed':
        courses = completedCourses;
        break;
      default:
        courses = enrolledCourses;
    }

    if (searchTerm) {
      courses = courses.filter(course =>
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return courses;
  };

  const tabs = [
    { id: 'all', label: 'All Courses', count: enrolledCourses.length },
    { id: 'in-progress', label: 'In Progress', count: inProgressCourses.length },
    { id: 'completed', label: 'Completed', count: completedCourses.length }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">My Learning</h1>
        <p className="text-gray-600">Track your learning progress and continue where you left off</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Enrolled</p>
              <p className="text-2xl font-bold text-gray-900">{enrolledCourses.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-yellow-50 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-gray-900">{inProgressCourses.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{completedCourses.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search your courses..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="flex gap-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </nav>
      </div>

      {/* Courses Grid */}
      {getFilteredCourses().length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {getFilteredCourses().map(course => (
            <div key={course.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
              {/* Course Image */}
              <div className="h-48 bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center relative">
                {course.thumbnail ? (
                  <img 
                    src={course.thumbnail} 
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <BookOpen className="w-16 h-16 text-gray-300" />
                )}

                {/* Progress Badge */}
                {course.progress.completionPercentage === 100 ? (
                  <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Completed
                  </div>
                ) : (
                  <div className="absolute top-3 right-3 bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    {course.progress.completionPercentage}%
                  </div>
                )}
              </div>

              {/* Course Info */}
              <div className="p-6">
                <div className="text-sm text-blue-600 font-medium mb-2">
                  {course.category}
                </div>
                
                <h3 className="font-bold text-gray-900 text-lg mb-2 line-clamp-2">
                  {course.title}
                </h3>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                    <span>{course.progress.completionPercentage}% complete</span>
                    <span>
                      {course.progress.completedLessons?.length || 0} of {course.lessonCount} lessons
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all"
                      style={{ width: `${course.progress.completionPercentage}%` }}
                    />
                  </div>
                </div>

                {/* Action Button */}
                <Link
                  to={`/course/${course.id}`}
                  className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  {course.progress.completionPercentage === 100 ? (
                    <>
                      <Download className="w-4 h-4" />
                      Review Course
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      Continue Learning
                    </>
                  )}
                </Link>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {activeTab === 'all' ? 'No courses enrolled yet' : 
             activeTab === 'in-progress' ? 'No courses in progress' : 
             'No completed courses'}
          </h3>
          <p className="text-gray-600 mb-6">
            {activeTab === 'all' ? 'Discover and enroll in courses to start learning' :
             activeTab === 'in-progress' ? 'Start a course to see it here' :
             'Complete your first course to see it here'}
          </p>
          {activeTab === 'all' && (
            <Link
              to="/discover"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              <BookOpen className="w-5 h-5" />
              Discover Courses
            </Link>
          )}
        </div>
      )}
    </div>
  );
};